import React, { useMemo } from "react"

import { AlertTriangle, DollarSign } from "lucide-react"

import CostBreakdownCard, { type CostLine } from "@/components/shared/CostBreakdownCard"
import {
  calculateDurationMinutes,
  formatChargingMilesBreakdown,
  formatCost,
  priceRoute,
  type VehicleCapacityInfo,
} from "@/lib/routeCostCalculation"
import { Route } from "@/types/collections"

import LoadFactorCard from "./LoadFactorCard"
import { DirectInjectionCostSection, HaulageRevenueSection } from "./StopMoneySection"
import { isDirectInjection, isHaulageDelivery } from "./stopKinds"

type RouteCost = NonNullable<ReturnType<typeof priceRoute>>

interface RouteCostTabProps {
  route: Route
  /** Null when the route can't be priced yet: no vehicle type, config, distance or duration. */
  routeCost: RouteCost | null
  plannedVehicleType?: string | null
  vehicleCapacityInfo: VehicleCapacityInfo | null
}

const RouteCostTab: React.FC<RouteCostTabProps> = ({
  route,
  routeCost,
  plannedVehicleType,
  vehicleCapacityInfo,
}) => {
  const totalForecastVolume = useMemo(() => {
    if (!route.stops || route.stops.length === 0) return null
    return route.stops.reduce((sum, stop) => sum + (stop.volumeEstimated || 0), 0)
  }, [route.stops])

  const haulageStops = useMemo(() => (route.stops ?? []).filter(isHaulageDelivery), [route.stops])
  const directInjectionStops = useMemo(
    () => (route.stops ?? []).filter(isDirectInjection),
    [route.stops],
  )

  return (
    <div className="space-y-4 px-5 py-4">
      {haulageStops.length > 0 && <HaulageRevenueSection stops={haulageStops} />}
      {directInjectionStops.length > 0 && (
        <DirectInjectionCostSection stops={directInjectionStops} />
      )}

      {routeCost && (
        <CostBreakdownCard
          density="full"
          title="Calculated Route Cost"
          subtitle="Breakdown of route expenses"
          lines={routeCostLines(route, routeCost)}
          total={routeCost.total_cost}
          revenue={routeCost.total_haulage_delivery_revenue}
          hideRevenueRow
          perParcel={{ cost: routeCost.cost_per_parcel, parcels: totalForecastVolume }}
        />
      )}

      {!routeCost && route.estimatedCost && (
        <div className="rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 p-3">
          <div className="mb-1 flex items-center gap-2 text-xs text-palette-jungleGreen500">
            <DollarSign size={12} />
            Estimated Route Cost
          </div>
          <div className="font-medium text-palette-jungleGreen900">
            {formatCost(route.estimatedCost)}
          </div>
        </div>
      )}

      {!routeCost && !route.estimatedCost && route.vehicleType && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center gap-2 text-xs text-amber-700">
            <AlertTriangle size={12} />
            <span>
              Cost calculation requires distance and duration from Samsara.
              {!route.distanceMiles && !route.durationMinutes && " Waiting for route data..."}
              {route.distanceMiles && !route.durationMinutes && " Waiting for duration data..."}
              {!route.distanceMiles && route.durationMinutes && " Waiting for distance data..."}
            </span>
          </div>
        </div>
      )}

      {!routeCost && !route.vehicleType && !plannedVehicleType && (
        <div className="rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 p-3">
          <div className="flex items-center gap-2 text-xs text-palette-jungleGreen500">
            <AlertTriangle size={12} />
            <span>
              Vehicle type not set. Configure vehicle type in route planning to calculate costs.
            </span>
          </div>
        </div>
      )}

      <LoadFactorCard
        capacity={vehicleCapacityInfo}
        stops={route.stops}
        vehicleCapacity={route.vehicleCapacity}
      />
    </div>
  )
}

/**
 * A route priced by direct injection has no lease or charging line, and its agreed price is
 * reported as the wage line whatever the route's duration.
 */
function routeCostLines(route: Route, routeCost: RouteCost): CostLine[] {
  // Recalculated from planned times to handle midnight crossover.
  const duration =
    route.plannedStartTime && route.plannedEndTime
      ? calculateDurationMinutes(route.plannedStartTime, route.plannedEndTime)
      : route.durationMinutes
  const priced = routeCost.is_direct_injection_priced === true
  const lines: CostLine[] = []

  if (!priced) {
    lines.push({
      kind: "lease",
      label: "Vehicle Lease",
      detail: "Daily prorated",
      value: routeCost.vehicle_lease_cost,
    })
    if (route.distanceMiles != null && route.distanceMiles > 0) {
      lines.push({
        kind: "charging",
        label: "Charging Cost",
        detail:
          formatChargingMilesBreakdown(routeCost) ?? `${route.distanceMiles.toFixed(1)} miles`,
        value: routeCost.charging_cost,
      })
    }
  }
  if (priced || (duration != null && duration > 0)) {
    lines.push({
      kind: "wage",
      label: "Driver Wage",
      detail: priced
        ? "Agreed direct injection price"
        : `${Math.round(((duration as number) / 60) * 10) / 10}h duration`,
      value: routeCost.driver_wage_cost,
    })
  }
  return lines
}

export default RouteCostTab
